import { FC, ReactNode } from 'react';
import { MdOpenInNew } from 'react-icons/md';
import {
  MFlex,
  MIcon,
  MSpinner,
  MText,
} from '../../../../../components/Monetize';
import { DocumentFormatTag } from './DocumentFormatTag';

interface QuoteDocumentNameCellProps {
  mediaType?: string | null;
  fileName: string;
  isReplacing: boolean;
  isLoading?: boolean;
  docId: string;
  isInteractive?: boolean;
  onClick: () => void;
}

export const QuoteDocumentNameCell: FC<QuoteDocumentNameCellProps> = ({
  mediaType,
  fileName,
  isReplacing,
  isLoading = false,
  docId,
  isInteractive = true,
  onClick,
}) => {
  let nameContent: ReactNode;
  if (isReplacing) {
    nameContent = (
      <MText fontSize="sm" color="fg.link" cursor="default" truncate minW={0}>
        {fileName}
      </MText>
    );
  } else if (!isInteractive) {
    nameContent = (
      <MText fontSize="sm" title={fileName} truncate minW={0}>
        {fileName}
      </MText>
    );
  } else {
    nameContent = (
      <MFlex alignItems="center" fontSize="sm" gap={1} minW={0} maxW="100%">
        <MText
          role="link"
          tabIndex={isLoading ? -1 : 0}
          title={fileName}
          lineClamp={2}
          color="fg.link"
          cursor={isLoading ? 'default' : 'pointer'}
          onClick={isLoading ? undefined : onClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !isLoading) {
              onClick();
            }
          }}
          truncate
          display="block"
          _hover={
            isLoading
              ? undefined
              : {
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: 'semibold',
                }
          }
          _focusVisible={{ boxShadow: 'focus', outline: 'none' }}
        >
          {fileName}
        </MText>
        {isLoading ? (
          <MSpinner size="xs" flexShrink={0} />
        ) : (
          <MIcon
            as={MdOpenInNew}
            color="tPurple.base"
            cursor="pointer"
            fontSize="lg"
            flexShrink={0}
            onClick={onClick}
          />
        )}
      </MFlex>
    );
  }

  return (
    <MFlex
      minW={0}
      alignItems="center"
      gap={2}
      data-testid={`quote-document-name-${docId}`}
    >
      <DocumentFormatTag mediaType={mediaType} />
      {nameContent}
    </MFlex>
  );
};
